import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock } from "lucide-react";
import { Toaster, toast } from "sonner";
import { StoreHeader } from "../components/StoreHeader";
import { useCart } from "../context/CartContext";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { useSecurityPulse } from "../hooks/useSecurityPulse";
import { formatINR, resolveImg } from "../lib/format";
import { TIER_BENEFITS } from "../lib/tiers";
import { BUYER_BADGES } from "../lib/badges";
import { EliteBadge } from "../components/EliteBadge";
import { validateGstin } from "../lib/gst";
import { redirectIfPhonePe } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";

// Fallback (used only until /pricing/my-tier resolves) — the source of
// truth for tier + GST is Admin → Financial Rules, fetched at mount.
const TIER_RATE_FALLBACK = { super_stockist: 0.12, wholesaler: 0.08, dealer: 0.05, booking_agent: 0 };

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const Field = ({ label, testid, ...props }) => (
  <label className="block group">
    <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">{label}{props.required && " *"}</span>
    <input data-testid={testid} {...props} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors" />
  </label>
);

export default function Checkout() {
  const { items, subtotal, clear, setQty } = useCart();
  const { user, api } = useAuth();
  const pulse = useSecurityPulse(api);
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [ship, setShip] = useState({ shipping_name: "", shipping_phone: "", shipping_address: "", shipping_state: "", gst_number: "", project_name: "" });
  const [gstErr, setGstErr] = useState("");
  const onChange = (e) => {
    const { name, value } = e.target;
    setShip((s) => ({ ...s, [name]: value }));
    if (name === "gst_number") setGstErr("");
  };

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)).catch(() => {}); }, [api]);

  // Live rates from Admin → Financial Rules (falls back to the module-
  // level TIER_RATE_FALLBACK only if the endpoint fails).
  const [liveRates, setLiveRates] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/pricing/my-tier")
       .then((r) => { if (alive) setLiveRates(r.data); })
       .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  useEffect(() => {
    if (user && user.account_type === "buyer") {
      setShip((s) => ({
        ...s,
        shipping_name: s.shipping_name || user.profile?.business_name || user.name || "",
        shipping_phone: s.shipping_phone || user.profile?.phone || "",
        shipping_address: s.shipping_address || user.profile?.address || "",
        gst_number: s.gst_number || user.profile?.gst_number || "",
      }));
    }
  }, [user]);

  if (user === null) return <div className="min-h-screen bg-[#070707]" />;
  if (user === false) return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]"><StoreHeader />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <Lock className="mx-auto text-[#B87333]" size={28} />
        <h1 className="font-serif text-4xl mt-6">Sign in to check out</h1>
        <p className="font-body text-[#8A8A82] mt-3">Only registered Vinci buyers can place orders — and unlock tier pricing.</p>
        <div className="flex gap-3 justify-center mt-8">
          <Link to="/login" className="rounded-full bg-[#F5F5F0] text-black px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:bg-[#B87333] transition-colors">Sign in</Link>
          <Link to="/register/buyer" className="rounded-full border border-white/20 px-6 py-3 font-grotesk text-xs uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Register</Link>
        </div>
      </div>
    </div>
  );

  const isBuyer = user.account_type === "buyer";
  // Prefer the live Financial-Rules rate; fall back to the local table only
  // if the /pricing/my-tier round-trip hasn't finished yet.
  const rate = isBuyer
    ? (liveRates
        ? Number(liveRates.tier_discount_rate || 0)
        : (TIER_RATE_FALLBACK[user.buyer_category] || 0))
    : 0;
  // Per-item GST from the live GST-bucket map (cement / tmt / other).
  const gstMap = liveRates?.gst || { cement: 0.18, tmt: 0.18, other: 0.18 };
  const materialBucket = (cat) => {
    const c = (cat || "").toLowerCase();
    if (c.startsWith("cement")) return "cement";
    if (c.startsWith("tmt") || ["structural", "sheets", "binding_wire"].includes(c)) return "tmt";
    return "other";
  };
  const discount = Math.round(subtotal * rate);
  const gst = items.reduce((acc, it) => {
    const lineAmount = (Number(it.quantity) || 0) * (Number(it.price) || 0);
    const lineTaxable = Math.round(lineAmount * (1 - rate));
    const lineRate = Number(gstMap[materialBucket(it.category)] ?? 0.18);
    return acc + Math.round(lineTaxable * lineRate);
  }, 0);
  const taxable = subtotal - discount;
  const total = taxable + gst;
  const blendedGstPct = taxable > 0 ? Math.round((gst / taxable) * 100) : 18;
  const badge = BUYER_BADGES[user.buyer_category];

  const placeOrder = async (railChoice = "razorpay") => {
    if (!ship.shipping_name || !ship.shipping_phone || !ship.shipping_address || !ship.shipping_state || !ship.gst_number) {
      toast.error("Please complete all shipping & GST details, including delivery state."); return;
    }
    const ge = validateGstin(ship.gst_number);
    if (ge) { setGstErr(ge); toast.error(ge); return; }
    // ── Vinci Security Pulse · Purchase Order gate ──────────────────
    // Fresh 6-digit OTP → verified → mint short-lived token → forward
    // it in the X-Security-Pulse header on the guarded /orders POST.
    let pulseToken;
    try {
      pulseToken = await pulse.request("checkout", null);
    } catch { return; }   // buyer cancelled or pulse failed
    setLoading(railChoice);
    try {
      const { data } = await api.post(
        "/orders",
        { items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })), ...ship, gst_number: ship.gst_number.trim().toUpperCase() },
        { headers: { "X-Security-Pulse": pulseToken } },
      );
      const order = data.order;
      if (!data.payment_enabled) {
        clear();
        toast.success("Order placed. Our desk will send a payment link shortly.");
        nav("/order-success", { state: { order, pending: true } });
        return;
      }
      if (railChoice === "phonepe") {
        const pp = await api.post(`/orders/${order.id}/pay/phonepe`);
        if (pp.data?.redirect_url) { clear(); redirectIfPhonePe(pp.data); return; }
        throw new Error("PhonePe could not open — please choose Pay via Razorpay.");
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway."); setLoading(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: Math.round(order.total * 100),
        currency: "INR",
        name: "Vinci Inc.",
        description: `Order ${order.id.slice(0, 8)}`,
        order_id: order.razorpay_order_id,
        prefill: { name: ship.shipping_name, email: user.email, contact: ship.shipping_phone },
        theme: { color: "#B87333" },
        handler: async (resp) => {
          try {
            await api.post(`/orders/${order.id}/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clear();
            nav("/order-success", { state: { order } });
          } catch {
            toast.error("Payment verification failed. Contact support with your payment ID.");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not place order.");
      setLoading(false);
    }
  };

  if (items.length === 0) return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]"><StoreHeader />
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-4xl">Your cart is empty</h1>
        <Link to="/shop" className="inline-block mt-6 text-[#B87333] font-grotesk text-xs uppercase tracking-[0.2em] hover:underline">Browse products</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <Toaster position="bottom-right" theme="dark" richColors />
      <StoreHeader />
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 py-10">
        <Link to="/shop" className="inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#F5F5F0] transition-colors mb-8"><ArrowLeft size={14} /> Continue shopping</Link>
        <h1 className="font-serif text-4xl md:text-6xl tracking-[-0.02em] mb-12">Checkout</h1>

        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7">
            <h2 className="font-serif text-2xl mb-8">Shipping & GST details</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Field label="Consignee / business name" testid="ship-name" name="shipping_name" required value={ship.shipping_name} onChange={onChange} />
              <Field label="Phone" testid="ship-phone" name="shipping_phone" required value={ship.shipping_phone} onChange={onChange} />
              <div>
                <Field label="GST number" testid="ship-gst" name="gst_number" required value={ship.gst_number} onChange={onChange} onBlur={() => setGstErr(validateGstin(ship.gst_number))} />
                {gstErr && <p data-testid="ship-gst-error" className="font-body text-xs text-[#C0392B] mt-2">{gstErr}</p>}
              </div>
              <label className="block group">
                <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">Delivery state *</span>
                <select data-testid="ship-state" name="shipping_state" required value={ship.shipping_state} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors [&>option]:bg-[#0e0e0e]">
                  <option value="">Select state…</option>
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block group md:col-span-2">
                <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">Delivery address *</span>
                <textarea data-testid="ship-address" name="shipping_address" required rows={3} value={ship.shipping_address} onChange={onChange} className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors resize-none" />
              </label>
              <div className="md:col-span-2">
                <Field label="Project name (optional)" testid="ship-project" name="project_name" placeholder="e.g. Skyline Towers — Phase 2" value={ship.project_name} onChange={onChange} />
                <p className="font-body text-xs text-[#6f6f68] mt-2">Tag this order to a project to group it under “My Projects” in your account.</p>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-5">
            <div className="rounded-sm border border-white/10 p-8 sticky top-28">
              <div className="flex items-center justify-between mb-6"><h2 className="font-serif text-2xl">Order summary</h2><EliteBadge testid="checkout-elite-badge" compact /></div>
              {user?.vinci_elite && <div className="mb-5 border border-[#D8B23E]/40 bg-[#D8B23E]/5 px-3 py-2 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]" data-testid="checkout-elite-note">◆ Vinci Elite · priority fulfilment desk</div>}
              <div className="space-y-4 mb-6">
                {items.map((i) => (
                  <div key={i.id} className="flex items-center gap-3">
                    <img src={resolveImg(i.image)} alt={i.name} className="w-14 h-14 object-cover rounded-sm border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-sm leading-tight truncate">{i.name}</div>
                      <div className="font-body text-xs text-[#8A8A82]">{i.quantity} × {formatINR(i.price)}</div>
                    </div>
                    <div className="font-serif text-sm">{formatINR(i.price * i.quantity)}</div>
                  </div>
                ))}
              </div>

              {badge && (
                <div className="flex items-center gap-2 mb-4 rounded-full px-3 py-2" style={{ background: `${badge.glow}14` }}>
                  <badge.Icon size={14} style={{ color: badge.to }} />
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.18em]" style={{ color: badge.to }}>{badge.label} · {Math.round(rate * 100)}% tier discount</span>
                </div>
              )}

              <div className="space-y-2.5 border-t border-white/10 pt-5 font-body text-sm">
                <Row l="Subtotal" v={formatINR(subtotal)} />
                {discount > 0 && <Row l={`Tier discount (${Math.round(rate * 100)}%)`} v={`− ${formatINR(discount)}`} accent />}
                <Row l={`GST (${blendedGstPct}%)`} v={formatINR(gst)} />
                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <span className="font-serif text-lg">Total</span>
                  <span data-testid="order-total" className="font-serif text-3xl">{formatINR(total)}</span>
                </div>
              </div>

              <div className="mt-7" data-testid="place-order-rails">
                <PayRailButtons testId="place-order" onPay={placeOrder} busy={loading} amountLabel={formatINR(total)}
                                note="UPI, Cards and Netbanking on both rails. Your order confirms the moment the gateway verifies payment." />
              </div>
            </div>
          </div>
        </div>
      </div>
      {pulse.modal}
    </div>
  );
}

const Row = ({ l, v, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#8A8A82]">{l}</span>
    <span style={accent ? { color: "#2FB999" } : {}}>{v}</span>
  </div>
);
